import type EventEmitter from 'node:events';
import {type OrderCloneParams} from '../order';
import {type AlgoClonePayload} from '../algo';
import {type AuthInfo} from './authentication';
import {type MessageRequest} from './pubsub';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- Application contracts explicitly distinguish JSON null from omitted fields.
type NullValue = null;

/** Receives one application-service envelope with a caller-selected payload. */
export type ApplicationMessageListener<Payload = any> = (message: MessageRequest<Payload>) => void;

/** EventEmitter surface with payload-aware application listener registration. */
type ApplicationEventEmitter = Omit<EventEmitter, 'addListener' | 'removeListener'> & {
  addListener: <Payload = any>(topic: string, listener: ApplicationMessageListener<Payload>) => ApplicationEventEmitter;
  removeListener: <Payload = any>(topic: string, listener: ApplicationMessageListener<Payload>) => ApplicationEventEmitter;
};

export type ApplicationClientType = ApplicationEventEmitter & {
  _getWindowInfo: () => Promise<undefined>;
  APPLICATION_EVENTS: ApplicationEventsMap;
  application: {getApplications: (signal?: AbortSignal) => Promise<unknown[]>};
  authInfo?: AuthInfo | NullValue;
  browserContextId?: string;
  broadcast: <Data = unknown>(topic: string, data: Data) => Promise<void>;
  componentState?: Record<string, unknown>;
  createWorkersIframe: () => HTMLIFrameElement;
  getActiveWorkspace: () => Promise<MessageRequest>;
  getApplicationStatus: () => Promise<any>;
  getAuthInfo: () => Promise<AuthInfo | NullValue>;
  initialize: () => Promise<undefined>;
  onMessage: (message: MessageRequest) => undefined | void;
  publish: <Data = unknown>(topic: string, data: Data) => void;
  registerClient: () => Promise<undefined>;
  request: (message: Partial<MessageRequest>) => Promise<MessageRequest>;
  saveworkspace: (workspace: any) => Promise<MessageRequest>;
  sendMessage: (message: MessageRequest) => undefined | void;
  spawnData: AlgoClonePayload | OrderCloneParams | any;
  startHealthCheck: () => Promise<void>;
  state: {
    getComponentState: () => Promise<any>;
    saveComponentState: () => Promise<MessageRequest>;
    getComponentStateValue: (key: string) => Promise<any>;
    saveValueToComponentState: (key: string, value: string | number | boolean) => Promise<MessageRequest>;
    getGlobalState: (id: string) => Promise<any>;
    saveGlobalState: (key: string, value: any) => Promise<MessageRequest>;
    /**
     * @deprecated - unused
     */
    deleteComponentState: () => Promise<MessageRequest>;
  };
  status: string;
  subscribe: <Payload = any>(topic: string, cb: ApplicationMessageListener<Payload>) => void;
  subscribedTopics: Record<string, boolean>;
  unSubscribe: <Payload = any>(topic: string, cb: ApplicationMessageListener<Payload>) => void;
  windowID: string | undefined;
  windowParams: {
    browserContextId?: string;
    componentId?: ComponentId | string | NullValue;
    spawnDataId?: string | NullValue;
    type?: string;
    windowID?: string | NullValue;
  };
  workerName: string;
  workerPath: string;
};

export type ApplicationEventsMap = {
  PREFERENCES: 'preferences';
  SETTINGS: 'settings';
  WORKSPACES: 'workspaces';

  APPLICATIONS: {
    GET_APPLICATION: AppEventMessage;
    GET_APPLICATIONS: AppEventMessage;
  };
  AUTH: {
    AUTH_INFO: AppEventMessage;
    AUTH_STATUS: AppEventMessage;
    AUTH_STATUS_CHANGE: AppEventMessage;
    CHECK_AUTH: AppEventMessage;
    INITIALIZE: AppEventMessage;
    LOAD_AUTH_SCREEN: AppEventMessage;
    PUBLISH_AUTHORIZATION: AppEventMessage;
    LOGOUT: AppEventMessage;
  };
  HEALTH: {
    PING: AppEventMessage;
  };
  LINKER: {
    GET_LINKED_WINDOWS: AppEventMessage;
    GET_WINDOW_LINKS: AppEventMessage;
    REGISTER_WINDOW: AppEventMessage;
    REGISTER_WINDOW_LINK: AppEventMessage;
    UNREGISTER_WINDOW_LINK: AppEventMessage;
  };
  STATE: {
    DELETE_COMPONENT_STATE: AppEventMessage;
    GET_COMPONENT_STATE: AppEventMessage;
    GET_GLOBAL_STATE: AppEventMessage;
    SAVE_COMPONENT_STATE: AppEventMessage;
    SAVE_GLOBAL_STATE: AppEventMessage;
  };
  STATUS: {
    CHANGE: AppEventMessage;
    GET: AppEventMessage;
  };
  WINDOW: {
    CLOSE_WINDOW: AppEventMessage;
    GET_SPAWN_DATA: AppEventMessage;
    OPEN_WINDOW: AppEventMessage;
    SELECT_WINDOW: AppEventMessage;
    SHOW_WINDOW: AppEventMessage;
    SHOW_SCRIM: AppEventMessage;
    HIDE_SCRIM: AppEventMessage;
  };
  WORKSPACE: {
    GET_ACTIVE_WORKSPACE: AppEventMessage;
    SAVE_WORKSPACE: AppEventMessage;
  };

};

type AppEventMessage = {
  topic: string;
  messageType: ApplicationEventMessageType.Broadcast | ApplicationEventMessageType.Publish | ApplicationEventMessageType.Request;
};

export enum ApplicationEventMessageType {
  Broadcast = 'broadcast',
  Publish = 'publish',
  Request = 'request',
}

export type ComponentId =
  'algoManager' |
  'chart' |
  'inventory' |
  'monitoringScreen' |
  'options' |
  'orderBlotter' |
  'orderBook' |
  'orderEntry' |
  'orderSummary' |
  'tickerGrid';
